import type { Webviewer } from "lib";

import type { CommandMessage } from "./commandChannel";
import type { HostEventSubscriptions } from "./eventSubscriptions";

type SnapshotOptions = Parameters<Webviewer["camera"]["getSnapshotDataUrl"]>[0];

/**
 * The set of Webviewer operations a native host can reach over the command channel.
 *
 * Default-deny: anything not listed here throws, so the channel is opt-in per command rather than a
 * general remote-call surface. Dispatch is an explicit switch rather than an index into the viewer,
 * so the untrusted `module`/`command` strings never reach the object graph.
 *
 * Each command takes its input from named fields on the message rather than a positional list, so a
 * host writing one reads the field names instead of counting arguments. Snapshot options stay nested
 * under `options` because one of them is called `type`, which the message itself already uses.
 */
export function runCommand(webviewer: Webviewer, message: CommandMessage, events: HostEventSubscriptions) {
    const name = `${message.module}.${message.command}`;

    switch (name) {
        case "camera.setCamera":
            return webviewer.camera.setCamera(
                readNumbers(message.position, 3, name, "position"),
                readNumbers(message.rotation, 4, name, "rotation"),
            );
        case "camera.getCamera":
            return webviewer.camera.getCamera();
        case "camera.getSnapshotDataUrl":
            return webviewer.camera.getSnapshotDataUrl(message.options as SnapshotOptions);
        case "events.subscribe":
            return events.subscribe(message.event);
        case "events.unsubscribe":
            return events.unsubscribe(message.event);
        default:
            throw new Error(`Webviewer command "${name}" is not exposed`);
    }
}

/**
 * Message fields come from a native host, so they are validated before reaching the API. An
 * unchecked vector is padded to the expected length further down and lands in the render state as a
 * NaN pose, applied in an effect long after this command has already answered the caller with a
 * success.
 */
function readNumbers(value: unknown, length: 3, command: string, field: string): [number, number, number];
function readNumbers(value: unknown, length: 4, command: string, field: string): [number, number, number, number];
function readNumbers(value: unknown, length: number, command: string, field: string): number[] {
    if (
        !Array.isArray(value) ||
        value.length !== length ||
        value.some((entry) => typeof entry !== "number" || !Number.isFinite(entry))
    ) {
        throw new Error(`Webviewer command "${command}" expects ${field} to be ${length} finite numbers`);
    }

    return value;
}
